"use client";

import { locationcontext } from "@/lib/context";
import { useRouter } from "next/navigation";
import { startTransition, useEffect, useRef, useState } from "react";

export default function LocationWrapper({
  defaultLocation,
  defaultState,
  defaultCountry,
  defaultPincode,
  locationSelectorConfig,
  children,
}) {
  const [location, setLocation] = useState(defaultLocation || null);
  const [locationState, setLocationState] = useState(defaultState || null);
  const [locationCountry, setLocationCountry] = useState(
    defaultCountry || null,
  );
  const [locationPincode, setLocationPincode] = useState(
    defaultPincode || null,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(null);

  const locationClaimedRef = useRef(Boolean(defaultLocation));

  const router = useRouter();

  const updateLocationAndCookie = async (
    loc,
    shouldRedirect = true,
    geo = {},
    isInitialLoad = false,
  ) => {
    loc = loc?.replace(/\s+/g, "-");
    // The initial default-city claim runs while lazy page sections may still
    // be waiting on their chunks to hydrate. An urgent context update at that
    // point makes React discard their server HTML and client-render them
    // (empty, then filled — a layout shift). As a transition, React waits
    // for hydration instead. User-initiated changes stay urgent.
    const apply = isInitialLoad ? startTransition : (fn) => fn();
    try {
      apply(() => setLoading(true));
      const response = await fetch("/api/set-location", {
        method: "POST",
        body: JSON.stringify({ location: loc, ...geo }),
      });
      const {
        success,
        count,
        state: resolvedState,
        country: resolvedCountry,
      } = await response.json();

      if (success) {
        locationClaimedRef.current = true;
        apply(() => {
          setLocation(loc);
          setCount(count);
          // The API resolves state/country server-side when the caller
          // didn't supply them (e.g. an automatic default-city selection
          // with no geo data on hand) — prefer that over the raw geo arg.
          setLocationState(resolvedState || geo.state || null);
          setLocationCountry(resolvedCountry || geo.country || null);
          setLocationPincode(geo.pincode || null);
        });

        if (shouldRedirect) {
          router.push(`/${loc.toLowerCase()}/events`);
        } else if (!isInitialLoad) {
          router.refresh();
        }
      } else {
        console.error("Failed to resolve location for:", loc);
      }
    } catch (error) {
      console.error("Failed to set location:", error);
    } finally {
      apply(() => {
        setLoading(false);
        setIsOpen(false);
      });
    }
  };

  useEffect(() => {
    // If CitySync (mounted deeper, so its effect commits first) already
    // claimed this mount to adopt a city straight from the URL, back off —
    // otherwise this would race its in-flight request and could clobber
    // e.g. a fresh visitor's "surat" back to "ahmedabad" a moment later.
    if (locationClaimedRef.current) return;
    locationClaimedRef.current = true;
    updateLocationAndCookie("ahmedabad", false, {}, true);
  }, [defaultLocation]);

  return (
    <locationcontext.Provider
      value={{
        location,
        setLocation,
        locationState,
        locationCountry,
        locationPincode,
        isOpen,
        setIsOpen,
        loading,
        count,
        setCount,
        updateLocationAndCookie,
        locationClaimedRef,
        locationSelectorConfig: locationSelectorConfig || null,
      }}
    >
      {children}
    </locationcontext.Provider>
  );
}
